import React, { useCallback, useMemo, useReducer, useRef } from 'react';
import { InputCurrency } from '../../../../../components';
import { AlertWithTimestamp } from '../../../../../components/AlertWithTimestamp';
import { InputCurrencyProps } from '../../../../../components/InputCurrency/InputCurrency';
import { calculateEditableMarginLimit, formatEditableMarginLimit } from './marginLimit';

export type MarginInputProps = InputCurrencyProps & {
  cost: number | null | undefined;
  expensesPercent: number | null | undefined;
  salesTaxPercentage: number | null | undefined;
  onFallback: (focusValue: number | null) => void;
};

const MarginInput = ({ cost, expensesPercent, salesTaxPercentage, onFallback, onChangeEvent, onFocus, onBlur, ...props }: MarginInputProps) => {
  const invalidAttemptRef = useRef(false);
  const fallbackAppliedRef = useRef(false);
  const focusValueRef = useRef<number | null>(props.value);
  const [, forceInputRerender] = useReducer(count => count + 1, 0);

  const marginLimit = useMemo(() => calculateEditableMarginLimit(expensesPercent, salesTaxPercentage, cost), [cost, expensesPercent, salesTaxPercentage]);

  const handleChange = useCallback<NonNullable<InputCurrencyProps['onChangeEvent']>>(
    (event, maskedValue, value) => {
      if (marginLimit !== null && value > marginLimit) {
        fallbackAppliedRef.current = true;
        onFallback(focusValueRef.current);
        forceInputRerender();

        if (!invalidAttemptRef.current) {
          AlertWithTimestamp({
            message: `${formatEditableMarginLimit(marginLimit)}% é a margem máxima permitida para este produto`,
          });
          invalidAttemptRef.current = true;
        }

        return;
      }

      invalidAttemptRef.current = false;
      fallbackAppliedRef.current = false;
      onChangeEvent?.(event, maskedValue, value);
    },
    [marginLimit, onChangeEvent, onFallback],
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      invalidAttemptRef.current = false;
      fallbackAppliedRef.current = false;
      focusValueRef.current = props.value;
      onFocus?.(event);
    },
    [onFocus, props.value],
  );

  const handleBlur = useCallback<NonNullable<InputCurrencyProps['onBlur']>>(
    (event, value, details) => {
      const fallbackValue = focusValueRef.current;
      const blurValue = fallbackAppliedRef.current ? fallbackValue : value;
      const blurDetails = fallbackAppliedRef.current
        ? {
            hasChanges: false,
            onFocusValue: fallbackValue,
            onBlurValue: fallbackValue,
          }
        : details;

      invalidAttemptRef.current = false;
      fallbackAppliedRef.current = false;
      onBlur?.(event, blurValue, blurDetails);
    },
    [onBlur],
  );

  return <InputCurrency {...props} onChangeEvent={handleChange} onFocus={handleFocus} onBlur={handleBlur} />;
};

export default MarginInput;
